import { useState } from 'react'
import { api, ApiError, session } from '../lib/api'
import { useApi } from '../lib/hooks'
import { dateTime, money, num, shortDate } from '../lib/format'
import { Banner, Empty, ErrorNote, Loading, Panel, Pill, StatMini } from '../ui/kit'
import { FormDrawer, type FormFieldDef } from '../ui/FormDrawer'

interface BoardSummary {
  id: string; code: string; status: string
  effectiveFrom: string; effectiveTo: string | null
  publishedAt: string | null; publishedBy: string | null
  notes: string | null; lineCount: number
}

interface Grade { id: string; code: string; name: string; isSellable: boolean }
interface Crop { id: string; name: string; grades: Grade[] }
interface Zone { id: string; name: string }
interface Bundle { crops: Crop[]; zones: Zone[] }

/** One row being typed into a new board. */
interface DraftLine {
  cropId: string; gradeId: string; zoneId: string
  buyPricePerKg: string; sellPricePerKg: string; floorPricePerKg: string
}

interface BoardLine {
  cropId: string; cropName: string; cropCode: string
  gradeId: string; gradeCode: string
  buyPricePerKg: number; sellPricePerKg: number | null
  floorPricePerKg: number | null; upsideSharePct: number | null
  grossSpreadPerKg: number | null; grossMarginPct: number | null
}

interface Board {
  id: string; code: string; effectiveFrom: string; effectiveTo: string | null
  publishedAt: string; publishedBy: string | null
  indexSource: string | null; collarLowPct: number | null; collarHighPct: number | null
  lines: BoardLine[]
}

interface Spread {
  cropId: string; cropName: string
  observedFarmgate: number | null; observedWholesale: number | null; observedBroker: number | null
  boardBuyPrice: number | null; actualPaid: number | null; observations: number
}

export function Pricing() {
  const canWrite = session.has('admin', 'ops')

  const board = useApi<Board>('/pricing/current')
  const boards = useApi<BoardSummary[]>('/pricing/boards')
  const spread = useApi<Spread[]>('/pricing/spread?days=14')

  const [drafting, setDrafting] = useState(false)
  const [banner, setBanner] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null)

  async function publish(bd: BoardSummary) {
    const question =
      'Publish ' + bd.code + '?\n\n' +
      bd.lineCount + ' price' + (bd.lineCount === 1 ? '' : 's') +
      ', effective ' + shortDate(bd.effectiveFrom) + '. ' +
      'This supersedes the live board - every capture from now quotes these prices.'
    if (!window.confirm(question)) return
    try {
      await api.post(`/pricing/boards/${bd.id}/publish`)
      setBanner({ tone: 'ok', text: `${bd.code} is live. The field app picks it up on its next sync.` })
      board.reload(); boards.reload()
    } catch (e) {
      setBanner({ tone: 'crit', text: e instanceof ApiError ? e.message : 'That did not work.' })
    }
  }

  const b = board.data
  const avgMargin = b && b.lines.length > 0
    ? b.lines.reduce((s, l) => s + (Number(l.grossMarginPct) || 0), 0) / b.lines.filter((l) => l.grossMarginPct !== null).length
    : null

  return (
    <main className="page">
      <div className="page-head">
        <h1>Prices</h1>
        <p>
          What you pay farmers and what you charge buyers, per kilo, per grade. Publishing a new
          price list replaces the old one everywhere at once, so an agent in the field always quotes
          the price you set here — and every collection remembers what it was bought at.
        </p>
      </div>

      <div className="stack">
        {banner && (
          <Banner tone={banner.tone === 'ok' ? 'info' : 'crit'} title={banner.text}>{null}</Banner>
        )}
        {board.error && <ErrorNote error={board.error} onRetry={board.reload} />}
        {board.loading && <Panel><Loading rows={5} /></Panel>}

        {b === null && !board.loading && !board.error && (
          <Panel>
            <Empty
              title="No prices published"
              action={canWrite ? <button className="btn" onClick={() => setDrafting(true)}>Set prices</button> : undefined}
            >
              Until you publish prices, agents cannot buy anything — the app refuses rather than
              letting someone guess a price at the farm gate.
            </Empty>
          </Panel>
        )}

        {b && (
          <>
            <Panel
              title={<h3 className="mono">{b.code}</h3>}
              meta={
                <>
                  published {dateTime(b.publishedAt)}
                  {b.publishedBy && <> by {b.publishedBy}</>}
                </>
              }
              footer={
                <>
                  <StatMini label="Prices" value={b.lines.length} />
                  <StatMini label="Avg margin" value={avgMargin === null ? '—' : `${avgMargin.toFixed(1)}%`} />
                  <StatMini label="Based on" value={b.indexSource ?? 'none'}
                    note={b.collarLowPct !== null ? `collar ${b.collarLowPct}% / +${b.collarHighPct}%` : 'no collar set'} />
                  <StatMini label="Effective from" value={dateTime(b.effectiveFrom)} />
                </>
              }
            >
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Crop</th><th>Grade</th>
                      <th className="n">You pay / kg</th><th className="n">Floor</th>
                      <th className="n">You charge / kg</th><th className="n">Spread</th>
                      <th className="n">Gross margin</th><th className="n">Farmer's share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {b.lines.map((l) => {
                      const margin = Number(l.grossMarginPct ?? 0)
                      return (
                        <tr key={`${l.cropId}-${l.gradeId}`}>
                          <td className="strong">{l.cropName}</td>
                          <td><Pill dot={false}>{l.gradeCode}</Pill></td>
                          <td className="n">{money(l.buyPricePerKg, { currency: false })}</td>
                          <td className="n faint">{money(l.floorPricePerKg, { currency: false })}</td>
                          <td className="n">{money(l.sellPricePerKg, { currency: false })}</td>
                          <td className="n">{money(l.grossSpreadPerKg, { currency: false })}</td>
                          <td className="n">
                            <span className={margin >= 20 ? 'ok-t' : margin >= 12 ? 'warn-t' : 'crit-t'}>
                              {l.grossMarginPct === null ? '—' : `${margin.toFixed(1)}%`}
                            </span>
                          </td>
                          <td className="n faint">{l.upsideSharePct === null ? '—' : `${l.upsideSharePct}%`}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </Panel>

            <Banner tone="info" title="Gross spread is not margin">
              The spread above is the price of the work — collection, haulage, depot, last mile,
              shrink and credit all come out of it. Watch the contribution per kilo on the finance
              page, not this column.
            </Banner>
          </>
        )}

        <Panel
          title="Board against the market"
          meta="observations captured in the field, independently of the buying agent"
        >
          {spread.loading && <Loading rows={4} />}
          {spread.data && spread.data.every((s) => s.observations === 0) && (
            <Empty title="No market observations in the last 14 days">
              Field agents capture farmgate, wet-market and broker prices as they go. Without an
              independent feed you are quoting off yesterday's market while the brokers know today's.
            </Empty>
          )}
          {spread.data && spread.data.some((s) => s.observations > 0) && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Crop</th><th className="n">Seen at farms</th><th className="n">Wholesale</th>
                    <th className="n">Broker</th><th className="n">Your price</th>
                    <th className="n">Actually paid</th><th className="n">Observations</th>
                  </tr>
                </thead>
                <tbody>
                  {spread.data.map((s) => {
                    const gap = s.actualPaid !== null && s.observedFarmgate !== null
                      ? Number(s.actualPaid) - Number(s.observedFarmgate)
                      : null
                    return (
                      <tr key={s.cropId} className={gap !== null && gap > 5 ? 'attention' : ''}>
                        <td className="strong">{s.cropName}</td>
                        <td className="n">{money(s.observedFarmgate, { currency: false })}</td>
                        <td className="n">{money(s.observedWholesale, { currency: false })}</td>
                        <td className="n">{money(s.observedBroker, { currency: false })}</td>
                        <td className="n">{money(s.boardBuyPrice, { currency: false })}</td>
                        <td className="n">
                          {money(s.actualPaid, { currency: false })}
                          {gap !== null && gap > 5 && <> <Pill tone="warn" dot={false}>+{gap.toFixed(1)} over</Pill></>}
                        </td>
                        <td className="n faint">{num(s.observations)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel
          title="Boards"
          meta={canWrite && <button className="btn" onClick={() => setDrafting(true)}>Draft a board</button>}
        >
          {boards.loading && <Loading rows={3} />}
          {boards.error && <ErrorNote error={boards.error} onRetry={boards.reload} />}
          {boards.data?.length === 0 && (
            <Empty title="No price lists yet">
              Make one, put a price on every crop and grade you buy, then publish it.
            </Empty>
          )}
          {boards.data && boards.data.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Price list</th><th>Status</th><th className="n">Lines</th>
                    <th className="n">Effective from</th><th className="n">Until</th>
                    <th>Published by</th>
                    {canWrite && <th style={{ width: 90 }} />}
                  </tr>
                </thead>
                <tbody>
                  {boards.data.map((bd) => (
                    <tr key={bd.id} className={bd.status === 'draft' ? 'attention' : ''}>
                      <td className="code strong">{bd.code}</td>
                      <td>
                        <Pill tone={bd.status === 'published' ? 'ok' : bd.status === 'draft' ? 'warn' : 'neutral'}>
                          {bd.status}
                        </Pill>
                      </td>
                      <td className="n">{bd.lineCount}</td>
                      <td className="n">{shortDate(bd.effectiveFrom)}</td>
                      <td className="n faint">{bd.effectiveTo ? shortDate(bd.effectiveTo) : 'until superseded'}</td>
                      <td className="truncate faint">{bd.publishedBy ?? '\u2014'}</td>
                      {canWrite && (
                        <td style={{ textAlign: 'right' }}>
                          {bd.status === 'draft' && (
                            <button className="btn" style={smallBtn} onClick={() => void publish(bd)}>
                              Publish
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {drafting && (
        <DraftBoard
          onClose={() => setDrafting(false)}
          onDone={(code) => {
            setDrafting(false)
            setBanner({ tone: 'ok', text: `${code} saved as a draft. Nothing changes until you publish it.` })
            boards.reload()
          }}
        />
      )}
    </main>
  )
}

// ---------------------------------------------------------------- draft a board
function DraftBoard({ onClose, onDone }: { onClose: () => void; onDone: (code: string) => void }) {
  const ref = useApi<Bundle>('/reference/bundle', { cacheKey: 'reference.bundle' })
  const current = useApi<Board>('/pricing/current')

  const [values, setValues] = useState<Record<string, unknown>>({
    effectiveFrom: new Date(Date.now() + 3600000).toISOString().slice(0, 16),
  })
  const [lines, setLines] = useState<DraftLine[]>([])
  const set = (name: string, v: unknown) => setValues((s) => ({ ...s, [name]: v }))

  const crops = ref.data?.crops ?? []
  const zones = ref.data?.zones ?? []

  /** Start from the live board rather than a blank sheet - most days move a few prices. */
  function copyCurrent() {
    const b = current.data
    if (!b) return
    setLines(b.lines.map((l) => ({
      cropId: l.cropId, gradeId: l.gradeId, zoneId: '',
      buyPricePerKg: String(l.buyPricePerKg),
      sellPricePerKg: l.sellPricePerKg === null ? '' : String(l.sellPricePerKg),
      floorPricePerKg: l.floorPricePerKg === null ? '' : String(l.floorPricePerKg),
    })))
  }

  const fields: FormFieldDef[] = [
    { name: 'effectiveFrom', label: 'Effective from', type: 'datetime', required: true, half: true },
    {
      name: 'effectiveTo', label: 'Until', type: 'datetime', half: true,
      hint: 'Empty means until the next board supersedes it',
    },
    {
      name: 'indexSource', label: 'Based on', half: true, placeholder: 'e.g. Wakulima market',
      hint: 'Where these prices came from, for your own record',
    },
    {
      name: 'collarLowPct', label: 'Agents may vary by', type: 'decimal', half: true,
      suffix: '%', hint: 'How far off your price an agent may go at the farm gate',
    },
    { name: 'notes', label: 'Why these prices', type: 'textarea' },
  ]

  const usable = lines.filter((l) => l.cropId && l.gradeId && Number(l.buyPricePerKg) > 0)

  async function submit() {
    if (usable.length === 0) throw new Error('A board needs at least one priced line.')
    const res = await api.post<{ code: string }>('/pricing/boards', {
      effectiveFrom: new Date(String(values.effectiveFrom)).toISOString(),
      effectiveTo: values.effectiveTo ? new Date(String(values.effectiveTo)).toISOString() : null,
      indexSource: values.indexSource || null,
      collarLowPct: values.collarLowPct ? Number(values.collarLowPct) : null,
      collarHighPct: values.collarLowPct ? Number(values.collarLowPct) : null,
      notes: values.notes || null,
      lines: usable.map((l) => ({
        cropId: l.cropId,
        gradeId: l.gradeId,
        zoneId: l.zoneId || null,
        buyPricePerKg: Number(l.buyPricePerKg),
        sellPricePerKg: l.sellPricePerKg ? Number(l.sellPricePerKg) : null,
        floorPricePerKg: l.floorPricePerKg ? Number(l.floorPricePerKg) : null,
        upsideSharePct: null,
      })),
    })
    onDone(res.code)
  }

  return (
    <FormDrawer
      wide kind="Draft" title="Price board" submitLabel="Save draft"
      fields={fields} values={values} onChange={set}
      onSubmit={submit} onClose={onClose}
      footer={<>{usable.length} priced line{usable.length === 1 ? '' : 's'}</>}
    >
      <div className="spread" style={{ marginTop: 'var(--sp-7)', marginBottom: 'var(--sp-3)' }}>
        <span className="micro">Prices</span>
        {current.data && (
          <button type="button" className="btn quiet" style={smallBtn} onClick={copyCurrent}>
            Copy today's prices
          </button>
        )}
      </div>

      {lines.length === 0 && (
        <p className="hint">
          Nothing priced yet. Copy today's prices and change the few that moved, or add them one at
          a time.
        </p>
      )}

      {lines.map((l, i) => {
        const crop = crops.find((c) => c.id === l.cropId)
        return (
          <div className="line-row" key={i}>
            <div className="grow">
              <div className="field">
                <label>Crop</label>
                <select className="input" value={l.cropId}
                  onChange={(e) => setLines((s) => s.map((x, j) =>
                    j === i ? { ...x, cropId: e.target.value, gradeId: '' } : x))}>
                  <option value="">- choose -</option>
                  {crops.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="narrow">
              <div className="field">
                <label>Grade</label>
                <select className="input" value={l.gradeId} disabled={!crop}
                  onChange={(e) => setLines((s) => s.map((x, j) =>
                    j === i ? { ...x, gradeId: e.target.value } : x))}>
                  <option value="">-</option>
                  {(crop?.grades ?? []).map((g) => (
                    <option key={g.id} value={g.id}>{g.code}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="narrow">
              <div className="field">
                <label>Area</label>
                <select className="input" value={l.zoneId}
                  onChange={(e) => setLines((s) => s.map((x, j) =>
                    j === i ? { ...x, zoneId: e.target.value } : x))}>
                  <option value="">all</option>
                  {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
              </div>
            </div>
            <div className="narrow">
              <div className="field">
                <label>Buy</label>
                <input className="input n" type="number" step="0.01" value={l.buyPricePerKg}
                  onChange={(e) => setLines((s) => s.map((x, j) =>
                    j === i ? { ...x, buyPricePerKg: e.target.value } : x))} />
              </div>
            </div>
            <div className="narrow">
              <div className="field">
                <label>Sell</label>
                <input className="input n" type="number" step="0.01" value={l.sellPricePerKg}
                  onChange={(e) => setLines((s) => s.map((x, j) =>
                    j === i ? { ...x, sellPricePerKg: e.target.value } : x))} />
              </div>
            </div>
            <button type="button" className="line-drop" aria-label="Remove line"
              onClick={() => setLines((s) => s.filter((_, j) => j !== i))}>x</button>
          </div>
        )
      })}

      <button type="button" className="line-add"
        onClick={() => setLines((s) => [...s, {
          cropId: '', gradeId: '', zoneId: '',
          buyPricePerKg: '', sellPricePerKg: '', floorPricePerKg: '',
        }])}>
        + Add a price
      </button>

      <p className="hint" style={{ marginTop: 'var(--sp-6)' }}>
        A row with no area set is your price everywhere. Add a row for one area to pay differently
        there.
      </p>
    </FormDrawer>
  )
}

const smallBtn = { minHeight: 26, padding: '2px 10px', fontSize: 'var(--fs-xs)' } as const
